import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';

/**
 * A live progress update for a reservation save, shown on the save spinner.
 * `phase` mirrors the backend ChangeRequestProgress phases plus a client-only `slow` phase.
 */
export interface CacheStep {
    name: string;
    done: boolean;
}

export interface SaveProgressInfo {
    phase: string; // 'processing' | 'finalizing' | 'complete' | 'failed' | 'slow'
    label: string;
    detail?: string;
    error?: string;
    caches?: CacheStep[];
}

interface ChangeRequestProgressPayload {
    changeRequestId: string;
    phase: string;
    label: string;
    detail?: string | null;
    error?: string | null;
    caches?: CacheStep[] | null;
}

const POLL_INTERVAL_MS = 1000;
// How long to linger on the "all cache substeps ticked" frame before collapsing the step.
const COMPLETE_HOLD_MS = 300;
// Slow nudge: after this long without a confirmed save, tell the user it is still working. The flow
// keeps running server-side (a heavy save can take minutes), and live progress events keep updating
// the checklist while we wait.
const SLOW_NUDGE_MS = 90000;
// Hard ceiling: if the save hasn't confirmed by now, stop waiting and show a failure, so the spinner
// can never stay up forever (covers backend failure modes that don't clear InProgress, e.g. a flow
// that exhausts its retries). Generous: long saves run for minutes, and the status poll clears
// InProgress on failure, so a real failure surfaces well before this.
const HARD_TIMEOUT_MS = 15 * 60_000;

// One shared hub connection for the page, lazily started on the first tracked save and reused after
// (mirrors the tenant app's usePipelineHub singleton). Cookie auth flows automatically on same-origin.
let sharedConnection: HubConnection | null = null;
let starting: Promise<HubConnection> | null = null;

async function getConnection(): Promise<HubConnection> {
    if (sharedConnection?.state === HubConnectionState.Connected) {
        return sharedConnection;
    }
    if (!starting) {
        starting = (async () => {
            const conn = new HubConnectionBuilder()
                .withUrl('/hubs/pipeline')
                .withAutomaticReconnect([0, 1000, 2000, 5000])
                .build();
            await conn.start();
            sharedConnection = conn;
            return conn;
        })().finally(() => { starting = null; });
    }
    return starting;
}

/**
 * Tracks a save's progress until it completes or fails.
 *
 * Primary path: SignalR ChangeRequestProgress events (live phase labels + the failure reason).
 * Fallback: the /in-progress poll, so it still resolves if the socket is unavailable - the backend
 * now clears InProgress on failure too, so the poll no longer spins forever. A "slow" nudge fires if
 * nothing has happened for a while, so the user is never left with a silent spinner.
 */
export async function trackSaveProgress(
    reservationId: string,
    changeRequestId: string,
    onProgress: (p: SaveProgressInfo) => void,
): Promise<'complete' | 'failed'> {
    let settled = false;
    let lastCaches: CacheStep[] | undefined;
    const cleanups: Array<() => void> = [];

    return await new Promise<'complete' | 'failed'>((resolve) => {
        const finish = (result: 'complete' | 'failed') => {
            if (settled) return;
            settled = true;
            cleanups.forEach(fn => { try { fn(); } catch { /* ignore */ } });
            resolve(result);
        };

        const finishComplete = () => { onProgress({ phase: 'complete', label: 'Saved' }); finish('complete'); };

        // On complete, briefly show every cache substep ticked before the step collapses, so the
        // transition isn't jarring. (The backend's complete can beat the final 4/4 poll, so we
        // synthesise the all-ticked frame here.) Purely cosmetic - the save is already done.
        const resolveComplete = () => {
            if (settled) return;
            if (lastCaches && lastCaches.length > 0) {
                onProgress({ phase: 'finalizing', label: 'Updating availability & reports…', caches: lastCaches.map(c => ({ name: c.name, done: true })) });
                const t = setTimeout(finishComplete, COMPLETE_HOLD_MS);
                cleanups.push(() => clearTimeout(t));
            } else {
                finishComplete();
            }
        };

        // --- SignalR live progress (primary) ---
        const handler = (payload: string) => {
            let p: ChangeRequestProgressPayload;
            try { p = JSON.parse(payload); } catch { return; }
            if (p.changeRequestId !== changeRequestId) return;
            if (p.phase === 'complete') { resolveComplete(); return; }
            if (p.phase === 'failed') {
                onProgress({ phase: p.phase, label: p.label, detail: p.detail ?? undefined, error: p.error ?? undefined });
                finish('failed');
                return;
            }
            if (p.caches) lastCaches = p.caches;
            onProgress({ phase: p.phase, label: p.label, detail: p.detail ?? undefined, error: p.error ?? undefined, caches: p.caches ?? undefined });
        };
        getConnection()
            .then(async (conn) => {
                if (settled) return;
                conn.on('ReceiveChangeRequestProgress', handler);
                cleanups.push(() => conn.off('ReceiveChangeRequestProgress', handler));
                try { await conn.invoke('SubscribeToStream', `cr:${changeRequestId}`); } catch { /* poll covers it */ }
            })
            .catch(() => { /* no hub - poll covers it */ });

        // --- Poll fallback (also resolves if the socket never connects) ---
        let pollTimer: ReturnType<typeof setTimeout> | null = null;
        const poll = async () => {
            if (settled) return;
            try {
                // The /status endpoint distinguishes failed from complete (both clear InProgress), so the
                // poll never masks a failed save as a success even if the SignalR event is missed.
                const res = await fetch(`/reservations/${reservationId}/change-requests/${changeRequestId}/status`);
                const status = (await res.text()).trim();
                if (status === 'failed') {
                    const error = res.headers?.get('X-Change-Request-Error')
                        || 'The save did not complete successfully. Please try again.';
                    onProgress({ phase: 'failed', label: 'Save failed', error });
                    finish('failed');
                    return;
                }
                if (status === 'complete') { resolveComplete(); return; }
                // 'in-progress' (or anything unexpected) → keep polling
            } catch { /* transient - keep trying */ }
            if (!settled) pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
        };
        void poll();
        cleanups.push(() => { if (pollTimer) clearTimeout(pollTimer); });

        // --- Slow nudge: keep waiting, but tell the user the save is still working ---
        const slowTimer = setTimeout(() => {
            onProgress({
                phase: 'slow',
                label: 'Still saving…',
                detail: 'This save is taking longer than usual. It is still processing and progress updates will keep appearing here.',
            });
        }, SLOW_NUDGE_MS);
        cleanups.push(() => clearTimeout(slowTimer));

        // --- Hard timeout: guarantees the spinner never stays up forever ---
        const hardTimer = setTimeout(() => {
            onProgress({
                phase: 'failed',
                label: 'Save timed out',
                error: 'The save did not confirm in time. It may still be processing - refresh to check.',
            });
            finish('failed');
        }, HARD_TIMEOUT_MS);
        cleanups.push(() => clearTimeout(hardTimer));
    });
}
